import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * <ImageSlot>
 * Renderiza uma imagem definida em `/api/image-slots/*`.
 * Fallback automático para o asset padrão em /public.
 *
 * Uso:
 *   <ImageSlot slot="dashboard_hero" className="w-full h-full object-cover" />
 *   <ImageSlot slot="dashboard_hero" as="bg" className="...">
 *     {conteúdo sobreposto}
 *   </ImageSlot>
 */

// cache leve em memória para evitar refetch entre montagens
let _cache = null;
let _pending = null;
async function loadSlots() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  _pending = api.get("/image-slots")
    .then((r) => { _cache = r.data?.items || []; return _cache; })
    .catch(() => { _cache = []; return _cache; })
    .finally(() => { _pending = null; });
  return _pending;
}

export function invalidateImageSlots() { _cache = null; }

export default function ImageSlot({
  slot,
  fallback,
  className = "",
  alt = "",
  as = "img",            // "img" | "bg"
  children,
  style,
  onError,
  ...rest
}) {
  const [src, setSrc] = useState(fallback || "");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    loadSlots().then((items) => {
      if (!alive) return;
      const it = items.find((i) => i.key === slot);
      if (it?.url) setSrc(it.url);
      else if (fallback) setSrc(fallback);
      setReady(true);
    });
    return () => { alive = false; };
  }, [slot, fallback]);

  if (as === "bg") {
    return (
      <div
        className={className}
        style={{
          ...(style || {}),
          backgroundImage: src ? `url(${src})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        {...rest}
      >
        {children}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      data-slot={slot}
      className={className}
      onError={(e) => {
        if (fallback && e.currentTarget.src !== fallback) {
          e.currentTarget.src = fallback;
        }
        onError?.(e);
      }}
      style={style}
      {...rest}
    />
  );
}
